"use client";

import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherArrowLeft,
  FeatherBuilding2,
  FeatherTrash,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { UpdateOrganisationDtoPlan } from "@/generated/model";
import {
  getFindOrganisationQueryControllerFindBySlugQueryKey,
  getListOrganisationsQueryControllerListQueryKey,
  useCreateOrganisationCommandControllerCreate,
  useDeleteOrganisationCommandControllerDelete,
  useFindOrganisationQueryControllerFindBySlug,
  useUpdateOrganisationCommandControllerUpdate,
} from "@/generated/organisations/organisations";

const organisationSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Nursery group name is required")
    .max(100, "Name must be less than 100 characters"),
  postcode: z
    .string()
    .trim()
    .min(1, "Postcode is required")
    .max(20, "Postcode must be less than 20 characters"),
  country: z
    .string()
    .trim()
    .min(1, "Country is required")
    .max(100, "Country must be less than 100 characters"),
  notes: z
    .string()
    .trim()
    .max(500, "Notes must be less than 500 characters")
    .optional(),
});

interface OrganisationFormProps {
  slug?: string;
  onSuccess?: () => void;
}

export function OrganisationForm({ slug, onSuccess }: OrganisationFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isPlatformAdmin } = useAuth();
  const mode = slug ? "edit" : "create";

  const [name, setName] = useState("");
  const [postcode, setPostcode] = useState("");
  const [country, setCountry] = useState("");
  const [notes, setNotes] = useState("");
  const [plan, setPlan] = useState<UpdateOrganisationDtoPlan>(
    UpdateOrganisationDtoPlan.LAUNCH,
  );
  const [maxNurseries, setMaxNurseries] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Fetch organisation data if in edit mode
  const { data: organisationData, isLoading: isLoadingOrganisation } =
    useFindOrganisationQueryControllerFindBySlug(slug || "", {
      query: {
        enabled: !!slug,
        retry: false,
        staleTime: 0,
      },
    });

  // Populate form when organisation data loads
  useEffect(() => {
    if (organisationData?.data) {
      const organisation = organisationData.data;
      setName(organisation.name || "");
      setPostcode(organisation.postcode || "");
      setCountry(organisation.country || "");
      setNotes(organisation.notes || "");
      setPlan(
        (organisation.plan as unknown as UpdateOrganisationDtoPlan) ??
          UpdateOrganisationDtoPlan.LAUNCH,
      );
      setMaxNurseries(organisation.maxNurseries ?? 1);
    }
  }, [organisationData?.data]);

  // Create organisation mutation
  const { mutate: createOrganisation, isPending: isCreating } =
    useCreateOrganisationCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Nursery group created successfully");
          const queryKey = getListOrganisationsQueryControllerListQueryKey({});
          await queryClient.invalidateQueries({ queryKey });
          await queryClient.refetchQueries({ queryKey });
          if (onSuccess) {
            onSuccess();
          } else {
            navigate({ to: "/organisations" });
          }
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to create nursery group";
          toast.error(errorMessage);
        },
      },
    });

  // Update organisation mutation
  const { mutate: updateOrganisation, isPending: isUpdating } =
    useUpdateOrganisationCommandControllerUpdate({
      mutation: {
        onSuccess: async () => {
          toast.success("Nursery group updated successfully");
          const listQueryKey = getListOrganisationsQueryControllerListQueryKey(
            {},
          );
          const detailQueryKey = slug
            ? getFindOrganisationQueryControllerFindBySlugQueryKey(slug)
            : null;

          await queryClient.invalidateQueries({ queryKey: listQueryKey });
          if (detailQueryKey) {
            await queryClient.invalidateQueries({ queryKey: detailQueryKey });
            await queryClient.refetchQueries({ queryKey: detailQueryKey });
          }
          await queryClient.refetchQueries({ queryKey: listQueryKey });
          if (onSuccess) {
            onSuccess();
          } else {
            navigate({ to: "/organisations" });
          }
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to update nursery group";
          toast.error(errorMessage);
        },
      },
    });

  // Delete nursery group mutation
  const { mutate: deleteOrganisation, isPending: isDeleting } =
    useDeleteOrganisationCommandControllerDelete({
      mutation: {
        onSuccess: async () => {
          toast.success("Nursery group deleted successfully");
          const queryKey = getListOrganisationsQueryControllerListQueryKey({});
          await queryClient.invalidateQueries({ queryKey });
          await queryClient.refetchQueries({ queryKey });
          navigate({ to: "/organisations" });
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to delete nursery group";
          toast.error(errorMessage);
        },
      },
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const result = organisationSchema.safeParse({
      name,
      postcode,
      country,
      notes: notes || undefined,
    });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        const path = err.path.join(".");
        fieldErrors[path] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    if (mode === "create") {
      createOrganisation({
        data: {
          name,
          postcode,
          country,
          notes: notes || undefined,
        },
      });
    } else if (slug) {
      updateOrganisation({
        slug,
        data: {
          name,
          postcode,
          country,
          notes: notes || undefined,
          plan,
          maxNurseries,
        },
      });
    }
  };

  const handleDelete = () => {
    if (!slug) return;
    deleteOrganisation({ slug });
  };

  const isLoading = isCreating || isUpdating || isLoadingOrganisation;
  const isSaving = isCreating || isUpdating;
  const isReadOnly = !isPlatformAdmin && mode === "edit";

  if (isLoadingOrganisation) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (mode === "edit" && !organisationData?.data) {
    return (
      <div className="flex w-full flex-col items-start gap-8 bg-white animate-fade-in">
        <div className="flex w-full flex-col items-start gap-1 px-8 py-8">
          <div className="flex w-full items-center gap-2">
            <IconButton
              icon={<FeatherArrowLeft />}
              onClick={() => navigate({ to: "/organisations" })}
            />
            <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
              Nursery group not found
            </span>
          </div>
          <span className="text-body font-body text-subtext-color">
            The nursery group you&apos;re looking for doesn&apos;t exist.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      <form
        onSubmit={handleSubmit}
        className="flex w-full flex-col items-start"
      >
        {/* Header */}
        <div className="flex w-full flex-col items-start gap-1 pb-8">
          <div className="flex w-full flex-wrap items-center gap-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <div className="flex w-full items-center gap-2">
                <IconButton
                  icon={<FeatherArrowLeft />}
                  onClick={() => navigate({ to: "/organisations" })}
                />
                <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                  {mode === "edit"
                    ? `Edit nursery group - ${organisationData?.data.name}`
                    : "Create nursery group"}
                </span>
              </div>
              <span className="text-body font-body text-subtext-color pl-10">
                {mode === "edit"
                  ? "Update nursery group details"
                  : "Add a new nursery group to the platform"}
              </span>
            </div>
            {isPlatformAdmin && (
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="neutral-primary"
                  size="large"
                  onClick={() => navigate({ to: "/organisations" })}
                >
                  Cancel
                </Button>
                <Button type="submit" size="large" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save changes"}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Form Content */}
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          {/* Organisation Details Section */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Nursery group details
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  Basic information about the nursery group
                </span>
              </div>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full min-w-[448px] flex-col items-start gap-6">
                <TextField
                  className="h-auto w-full"
                  size="large"
                  label="Nursery group name"
                  error={!!errors.name}
                  helpText={errors.name}
                >
                  <TextField.Input
                    id="name"
                    placeholder="e.g., Acme Corporation"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isLoading || isReadOnly}
                  />
                </TextField>

                <div className="flex w-full items-start gap-6">
                  <TextField
                    className="h-auto grow shrink-0 basis-0"
                    size="large"
                    label="Postcode"
                    error={!!errors.postcode}
                    helpText={errors.postcode}
                  >
                    <TextField.Input
                      id="postcode"
                      placeholder="e.g., SW1A 1AA"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                      disabled={isLoading || isReadOnly}
                    />
                  </TextField>

                  <TextField
                    className="h-auto grow shrink-0 basis-0"
                    size="large"
                    label="Country"
                    error={!!errors.country}
                    helpText={errors.country}
                  >
                    <TextField.Input
                      id="country"
                      placeholder="e.g., United Kingdom"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      disabled={isLoading || isReadOnly}
                    />
                  </TextField>
                </div>

                <TextArea
                  className="h-auto w-full"
                  label="Notes (optional)"
                  error={!!errors.notes}
                  helpText={errors.notes}
                >
                  <TextArea.Input
                    id="notes"
                    placeholder="Additional notes about this nursery group..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isLoading || isReadOnly}
                  />
                </TextArea>
              </div>
            </div>
          </div>

          {/* Plan Section (Platform Admin Only) */}
          {isPlatformAdmin && mode === "edit" && (
            <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
              <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
                <div className="flex w-full flex-col items-start">
                  <span className="w-full text-heading-3 font-heading-3 text-default-font">
                    Plan
                  </span>
                  <span className="w-full text-body font-body text-subtext-color">
                    The plan tier determines which features this nursery group
                    can access
                  </span>
                </div>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
                <div className="flex w-full min-w-[448px] flex-col items-start gap-6">
                  <Select
                    label="Plan tier"
                    value={plan}
                    onValueChange={(v) => {
                      if (v) setPlan(v as UpdateOrganisationDtoPlan);
                    }}
                    disabled={isSaving}
                  >
                    <Select.Item value="LAUNCH">Launch</Select.Item>
                    <Select.Item value="BOOST">Boost</Select.Item>
                    <Select.Item value="SCALE">Scale</Select.Item>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Branch Limit Section (Platform Admin Only) */}
          {isPlatformAdmin && mode === "edit" && (
            <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
              <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
                <div className="flex w-full flex-col items-start">
                  <span className="w-full text-heading-3 font-heading-3 text-default-font">
                    Branch limit
                  </span>
                  <span className="w-full text-body font-body text-subtext-color">
                    Maximum number of nurseries this nursery group can have
                  </span>
                </div>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
                <div className="flex w-full min-w-[448px] flex-col items-start gap-6">
                  <TextField
                    className="h-auto w-full max-w-[200px]"
                    size="large"
                    label="Maximum nurseries"
                    error={!!errors.maxNurseries}
                    helpText={errors.maxNurseries}
                  >
                    <TextField.Input
                      id="maxNurseries"
                      type="number"
                      min={1}
                      max={1000}
                      value={maxNurseries.toString()}
                      onChange={(e) =>
                        setMaxNurseries(
                          Math.max(1, parseInt(e.target.value) || 1),
                        )
                      }
                      disabled={isSaving}
                    />
                  </TextField>
                </div>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex w-full items-center justify-between border-t border-solid border-secondary-beige-200 pt-6">
            {mode === "edit" && isPlatformAdmin ? (
              <Button
                type="button"
                variant="destructive-secondary"
                size="large"
                icon={<FeatherTrash />}
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete nursery group
              </Button>
            ) : (
              <div />
            )}
            {isPlatformAdmin && (
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="neutral-primary"
                  size="large"
                  onClick={() => navigate({ to: "/organisations" })}
                >
                  Cancel
                </Button>
                <Button type="submit" size="large" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save changes"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <DialogTitle className="sr-only">Delete nursery group</DialogTitle>
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
              <div className="flex flex-col items-center gap-6">
                <IconWithLogoBackground
                  variant="error"
                  size="x-large"
                  icon={<FeatherBuilding2 />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center">
                    Delete nursery group?
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    Are you sure you want to delete <strong>{name}</strong>?
                    This action cannot be undone.
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
              <Button
                className="h-14 w-80 flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                className="h-14 w-80 flex-none"
                variant="destructive-primary"
                size="extra-large"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
